import { useEffect, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { ConfirmActionDialog } from "@/components/ConfirmActionDialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationBar } from "@/components/PaginationBar";
import { CreateApiKeyDialog } from "@/components/CreateApiKeyDialog";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { formatDateTime } from "@/lib/format";
import { BanIcon, PlusIcon } from "lucide-react";

type ApiKey = components["schemas"]["ApiKeyResponse"];

interface ApiKeysTableProps {
  clientId?: string;
}

export function ApiKeysTable({ clientId }: ApiKeysTableProps) {
  const pagination = useCursorPagination({ initialLimit: 20 });

  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [refetchKey, setRefetchKey] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchApiKeys() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/api-keys", {
        params: {
          query: {
            ClientId: clientId,
            Cursor: pagination.cursor,
            Limit: pagination.limit,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load API keys.");
      } else {
        setApiKeys(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchApiKeys();
    return () => {
      cancelled = true;
    };
  }, [clientId, pagination.cursor, pagination.limit, refetchKey]);

  async function handleRevoke(id: string) {
    setRevoking(id);
    const { error: apiError } = await api.POST("/admin/api-keys/{id}/revoke", {
      params: { path: { id } },
    });
    if (apiError) {
      setError("Failed to revoke API key.");
    } else {
      setRefetchKey((k) => k + 1);
    }
    setRevoking(null);
    setRevokeTarget(null);
  }

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        {clientId && (
          <Button onClick={() => setCreateOpen(true)}>
            <PlusIcon className="size-4" />
            Create API Key
          </Button>
        )}
        {loading && <Spinner className="size-5" />}
      </div>

      {clientId && (
        <CreateApiKeyDialog
          clientId={clientId}
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={() => setRefetchKey((k) => k + 1)}
        />
      )}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Prefix</TableHead>
              <TableHead>ID</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Revoked</TableHead>
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {apiKeys.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-muted-foreground h-24 text-center">
                  No API keys found.
                </TableCell>
              </TableRow>
            ) : (
              apiKeys.map((key) => {
                const isRevoked = !!key.revokedAt;
                return (
                  <TableRow key={key.id}>
                    <TableCell className="font-medium">
                      {key.name || <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="font-mono text-xs">
                        {key.keyPrefix}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="font-mono text-xs">
                        {key.id?.slice(0, 8)}...
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={isRevoked ? "destructive" : "default"}>
                        {isRevoked ? "Revoked" : "Active"}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDateTime(key.createdAt)}</TableCell>
                    <TableCell>{formatDateTime(key.expiresAt)}</TableCell>
                    <TableCell>{formatDateTime(key.revokedAt)}</TableCell>
                    <TableCell>
                      {!isRevoked && key.id && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          disabled={revoking === key.id}
                          onClick={() => setRevokeTarget(key)}
                          title="Revoke"
                        >
                          {revoking === key.id ? (
                            <Spinner className="size-4" />
                          ) : (
                            <BanIcon className="size-4" />
                          )}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />

      <ConfirmActionDialog
        open={!!revokeTarget}
        onOpenChange={(open) => { if (!open) setRevokeTarget(null); }}
        title="Revoke this API key?"
        description={
          revokeTarget?.name ? (
            <>Revoking <span className="font-medium">{revokeTarget.name}</span> ({revokeTarget.keyPrefix}) will immediately stop all requests that use it. This cannot be undone.</>
          ) : (
            <>Revoking <span className="font-mono">{revokeTarget?.keyPrefix}</span> will immediately stop all requests that use it. This cannot be undone.</>
          )
        }
        confirmLabel="Revoke"
        pending={!!revoking}
        onConfirm={() => { if (revokeTarget?.id) handleRevoke(revokeTarget.id); }}
      />
    </div>
  );
}
